import React from "react";
import {
  View,
  Text,
  Modal,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { Pen, Save, X } from "lucide-react";
import { formatTodayDate } from "../../../../../../../utils/timeUtils";
import InputDropdown from "../../../../../../common/native/InputDropdown";
import { SignatureModalProps } from "../../types/VoiceNotes";

const SignatureModal: React.FC<SignatureModalProps> = ({
  visible,
  isAlreadySigned,
  signatureName,
  signatureDateTimeOverride,
  savedSignatures,
  selectedSavedSignature,
  loadingSignatures,
  signingInProgress,
  savingSignature,
  canSaveSignature,
  isSignatureSaved,
  signatureError,
  onClose,
  onSignatureName,
  onSignatureDateTimeOverrideChange,
  onSelectSavedSignature,
  onSaveSignature,
  onSign,
}) => {
  const saveButtonLabel = isSignatureSaved ? "Saved" : "Save Signature";
  const signButtonLabel = isAlreadySigned ? "Sign Again" : "Sign";
  const signatureDateTimeInputStyle: React.CSSProperties = {
    width: "100%",
    border: `1px solid ${TOKENS.color.border}`,
    borderRadius: TOKENS.radius.r6,
    padding: `${TOKENS.spacing.s12}px`,
    fontSize: TOKENS.fontSize.s16,
    color: TOKENS.color.textPrimary,
    backgroundColor: TOKENS.color.background,
    boxSizing: "border-box",
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <Text style={styles.modalTitle}>
            {isAlreadySigned ? "Re-sign Document" : "Add Your Signature"}
          </Text>
          <Text style={styles.modalSubtitle}>
            {isAlreadySigned
              ? "Update the signature and optionally override the signed date and time."
              : "Please type your name below to sign this document."}
          </Text>

          <View style={styles.savedSignaturesContainer}>
            <Text style={styles.signatureLabel}>Saved signatures:</Text>
            {loadingSignatures ? (
              <View style={styles.signatureStatusRow}>
                <ActivityIndicator size="small" color={TOKENS.color.primary} />
                <Text style={styles.signatureStatusText}>
                  Loading saved signatures...
                </Text>
              </View>
            ) : savedSignatures.length > 0 ? (
              <InputDropdown
                name=""
                selections={savedSignatures}
                value={selectedSavedSignature}
                onSelect={onSelectSavedSignature}
                placeholder="Select a saved signature"
                formatLabel={(value) => value}
                dropdownContainerStyle={{
                  opacity: 1,
                  zIndex: TOKENS.zIndex.dropdownMenu,
                  elevation: TOKENS.zIndex.dropdownElevation,
                }}
              />
            ) : (
              <Text style={styles.signatureEmptyText}>
                No saved signatures yet.
              </Text>
            )}
            {signatureError ? (
              <Text style={styles.signatureErrorText}>{signatureError}</Text>
            ) : null}
          </View>

          <View style={styles.signatureInputContainer}>
            <Text style={styles.signatureLabel}>Signature:</Text>
            <TextInput
              style={styles.signatureInput}
              value={signatureName}
              onChangeText={onSignatureName}
              placeholder="Type your full name"
              autoCapitalize="words"
            />
          </View>

          <View style={styles.saveSignatureRow}>
            <TouchableOpacity
              style={[
                styles.saveButton,
                (!canSaveSignature || savingSignature) && styles.disabledButton,
              ]}
              onPress={onSaveSignature}
              disabled={!canSaveSignature || savingSignature}
            >
              {savingSignature ? (
                <ActivityIndicator
                  size="small"
                  color={TOKENS.color.inverseText}
                />
              ) : (
                <>
                  <Save size={16} color={TOKENS.color.inverseText} />
                  <Text style={styles.saveButtonText}>{saveButtonLabel}</Text>
                </>
              )}
            </TouchableOpacity>
            {isSignatureSaved && !savingSignature ? (
              <Text style={styles.savedHint}>Saved for reuse</Text>
            ) : null}
          </View>

          <View style={styles.signatureDateContainer}>
            <Text style={styles.signatureLabel}>Date:</Text>
            <Text style={styles.signatureDate}>{formatTodayDate()}</Text>
          </View>

          <View style={styles.signatureInputContainer}>
            <Text style={styles.signatureLabel}>
              Override signed date & time:
            </Text>
            <input
              style={signatureDateTimeInputStyle}
              type="datetime-local"
              value={signatureDateTimeOverride}
              onChange={(event) =>
                onSignatureDateTimeOverrideChange(event.target.value)
              }
            />
            <Text style={styles.signatureDateHint}>
              Leave blank to use the current date and time.
            </Text>
          </View>

          <View style={styles.modalActions}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={onClose}
              disabled={signingInProgress}
            >
              <X size={16} color={TOKENS.color.textMuted} />
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.signButton,
                signingInProgress && styles.disabledButton,
              ]}
              onPress={onSign}
              disabled={signingInProgress}
            >
              {signingInProgress ? (
                <ActivityIndicator
                  size="small"
                  color={TOKENS.color.inverseText}
                />
              ) : (
                <>
                  <Pen size={16} color={TOKENS.color.inverseText} />
                  <Text style={styles.signButtonText}>{signButtonLabel}</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const TOKENS = {
  color: {
    overlay: "rgba(0, 0, 0, 0.6)",
    background: "#FFFFFF",
    surfaceMuted: "#F5F5F5",
    border: "#DDDDDD",
    textPrimary: "#333333",
    textSecondary: "#666666",
    textMuted: "#555555",
    textError: "#E53935",
    primary: "#0085FF",
    inverseText: "#FFFFFF",
    neutralButton: "#F2F2F2",
  },
  spacing: {
    s4: 4,
    s8: 8,
    s12: 12,
    s16: 16,
    s20: 20,
    s24: 24,
    s32: 32,
  },
  radius: {
    r6: 6,
    r12: 12,
  },
  shadow: {
    modal: {
      shadowColor: "#000000",
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.2,
      shadowRadius: 12,
    },
  },
  font: {
    signature: "serif",
  },
  fontSize: {
    s14: 14,
    s16: 16,
    s20: 20,
  },
  lineHeight: {
    s14: 20,
    s16: 22,
    s20: 26,
  },
  zIndex: {
    modal: 1,
    savedSignatures: 10,
    dropdownMenu: 2000,
    dropdownElevation: 12,
  },
  motion: {
    fast: 100,
    standard: 200,
  },
  opacity: {
    disabled: 0.7,
  },
  size: {
    modalMaxWidth: 400,
  },
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: TOKENS.color.overlay,
    justifyContent: "center",
    alignItems: "center",
    padding: TOKENS.spacing.s20,
  },
  modalContent: {
    backgroundColor: TOKENS.color.background,
    borderRadius: TOKENS.radius.r12,
    padding: TOKENS.spacing.s24,
    width: "100%",
    maxWidth: TOKENS.size.modalMaxWidth,
    overflow: "visible",
    ...TOKENS.shadow.modal,
  },
  modalTitle: {
    fontSize: TOKENS.fontSize.s20,
    lineHeight: TOKENS.lineHeight.s20,
    fontWeight: "600",
    color: TOKENS.color.textPrimary,
    marginBottom: TOKENS.spacing.s8,
  },
  modalSubtitle: {
    fontSize: TOKENS.fontSize.s16,
    lineHeight: TOKENS.lineHeight.s16,
    color: TOKENS.color.textSecondary,
    marginBottom: TOKENS.spacing.s20,
  },
  savedSignaturesContainer: {
    marginBottom: TOKENS.spacing.s16,
    position: "relative",
    zIndex: TOKENS.zIndex.savedSignatures,
  },
  signatureStatusRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: TOKENS.spacing.s8,
  },
  signatureStatusText: {
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    color: TOKENS.color.textSecondary,
  },
  signatureEmptyText: {
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    color: TOKENS.color.textSecondary,
  },
  signatureErrorText: {
    marginTop: TOKENS.spacing.s8,
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    color: TOKENS.color.textError,
  },
  signatureInputContainer: {
    marginBottom: TOKENS.spacing.s12,
  },
  signatureLabel: {
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    fontWeight: "500",
    color: TOKENS.color.textPrimary,
    marginBottom: TOKENS.spacing.s8,
  },
  signatureInput: {
    backgroundColor: TOKENS.color.background,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.r6,
    padding: TOKENS.spacing.s12,
    fontSize: TOKENS.fontSize.s16,
    lineHeight: TOKENS.lineHeight.s16,
    color: TOKENS.color.textPrimary,
    fontFamily: TOKENS.font.signature,
    fontStyle: "italic",
  },
  saveSignatureRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: TOKENS.spacing.s12,
    marginBottom: TOKENS.spacing.s16,
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: TOKENS.color.primary,
    paddingVertical: TOKENS.spacing.s8,
    paddingHorizontal: TOKENS.spacing.s12,
    borderRadius: TOKENS.radius.r6,
    gap: TOKENS.spacing.s4,
  },
  saveButtonText: {
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    color: TOKENS.color.inverseText,
  },
  savedHint: {
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    color: TOKENS.color.textSecondary,
  },
  signatureDateContainer: {
    marginBottom: TOKENS.spacing.s24,
  },
  signatureDate: {
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    color: TOKENS.color.textPrimary,
    padding: TOKENS.spacing.s12,
    backgroundColor: TOKENS.color.surfaceMuted,
    borderRadius: TOKENS.radius.r6,
  },
  signatureDateHint: {
    marginTop: TOKENS.spacing.s8,
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    color: TOKENS.color.textSecondary,
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  cancelButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: TOKENS.color.neutralButton,
    paddingVertical: TOKENS.spacing.s8,
    paddingHorizontal: TOKENS.spacing.s12,
    borderRadius: TOKENS.radius.r6,
    marginRight: TOKENS.spacing.s8,
    gap: TOKENS.spacing.s4,
  },
  cancelButtonText: {
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    color: TOKENS.color.textMuted,
  },
  signButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: TOKENS.color.primary,
    paddingVertical: TOKENS.spacing.s8,
    paddingHorizontal: TOKENS.spacing.s12,
    borderRadius: TOKENS.radius.r6,
    gap: TOKENS.spacing.s4,
  },
  signButtonText: {
    fontSize: TOKENS.fontSize.s14,
    lineHeight: TOKENS.lineHeight.s14,
    color: TOKENS.color.inverseText,
  },
  disabledButton: {
    opacity: TOKENS.opacity.disabled,
  },
});

export default SignatureModal;
